import {
  useEffect,
  useRef,
  type ComponentType
} from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  useLocation,
  useOutletContext,
  useParams
} from 'react-router-dom';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { useGetAiKeywordsDetailsQuery } from '@/app/KeywordResearch/api/aiKeywordsApi';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';
import { plannerDetailsColumnConfig } from '../config/plannerDetailsColumnConfig';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type AgGridWrapperProps = {
  height: string;
  columns: unknown[];
  data: unknown[];
  settings?: Record<string, unknown>;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

const AiKeywordsDetails = () => {
  const { task_id, domain_slug, domain } = useParams<{
    task_id: string;
    domain_slug: string;
    domain: string;
  }>();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const location = useLocation();
  const gridRef = useRef<HTMLDivElement>(null);
  const pathname = location.pathname;

  const { data: aiKeywordsDetails, isLoading, isError, isFetching, error, refetch } =
    useGetAiKeywordsDetailsQuery(
      { id: task_id ?? '' },
      {
        skip: !task_id
      }
    );

  useEffect(() => {
    setBreadcrumbData([
      {
        text: 'کلمات AI',
        href: `/keyword-research/ai-keywords/history/${domain_slug}/${domain}`
      },
      { text: 'جزئیات', href: '#' }
    ]);
  }, [domain, domain_slug, setBreadcrumbData]);

  const queryError = getActiveQueryError([
    { isError, error, refetch, isFetching },
  ]);
  if (queryError) return <DataLoadError {...queryError} />;

  return (
    <Row>
      <Col>
        {isLoading || isFetching ? (
          <AgGridSkeleton
            height="696px"
            settings={{
              pagination: false,
              fullscreen: false,
              changeHeight: false,
              resetGrid: false,
              saveGrid: false,
              exportBtn: false,
              sideBar: false,
              masterDetail: null
            }}
          />
        ) : (
          <div ref={gridRef} className="for-style">
            {aiKeywordsDetails && aiKeywordsDetails.length > 0 ? (
              <TypedAgGridWrapper
                height="746px"
                columns={plannerDetailsColumnConfig(pathname)}
                data={aiKeywordsDetails || []}
                settings={{
                  pagination: true,
                  fullscreen: false,
                  changeHeight: false,
                  resetGrid: false,
                  saveGrid: false
                }}
              />
            ) : (
              <TypedAgGridWrapper
                height="696px"
                columns={plannerDetailsColumnConfig(pathname)}
                data={[]}
                settings={{
                  pagination: true,
                  fullscreen: false,
                  changeHeight: false,
                  resetGrid: false,
                  saveGrid: false
                }}
              />
            )}
          </div>
        )}
        <style>
          {`
        .for-style .ag-row-focus {
            z-index: 2;
      }`}
        </style>
      </Col>
    </Row>
  );
};

export default AiKeywordsDetails;
